import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CardsIcon } from "../components/CardsIcon";
import { Footer } from "../components/Footer";
import { Logo } from "../components/Logo";
import { TurnstileWidget } from "../components/TurnstileWidget";

const DEV_ACCOUNTS = [
  { role: "Joueur", email: "player01@allezle.fun" },
  { role: "Admin", email: "admin@leboncoin.tld" },
] as const;

export function LoginPage() {
  const { user, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      navigate(user.hasCompletedProfile ? "/home" : "/profile", {
        replace: true,
      });
    }
  }, [user, navigate]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const result = await requestMagicLink(email, turnstileToken);
      navigate("/login/sent", {
        state: {
          email: result.email,
          debugMagicLinkUrl: result.debugMagicLinkUrl,
        },
      });
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "Impossible d'envoyer le lien magique.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="flex w-full max-w-md flex-1 flex-col items-center justify-center">
        <CardsIcon className="mb-4 h-16 w-16" color="#FF5100" />
        <div className="card w-full text-center">
          <Logo className="mx-auto h-8" color="#FF5100" />
          <p className="mt-2 text-sm text-smoke">Connexion</p>

          {(import.meta.env.DEV || import.meta.env.VITE_ENV === "staging") && (
            <div className="mt-6 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-left">
              <p className="text-sm font-medium text-ink">
                Comptes de développement
              </p>
              <p className="mt-1 text-xs text-smoke">
                Choisis un compte pour préremplir l’email.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {DEV_ACCOUNTS.map((account) => (
                  <button
                    key={account.role}
                    type="button"
                    className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-left transition hover:border-ember focus:outline-none focus:ring-2 focus:ring-ember/20"
                    onClick={() => {
                      setEmail(account.email);
                      setError(null);
                    }}
                  >
                    <span className="block text-xs font-semibold text-ember">
                      {account.role}
                    </span>
                    <span className="mt-0.5 block break-all text-xs text-smoke">
                      {account.email}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form className="mt-6 space-y-5 text-left" onSubmit={handleSubmit}>
            <div>
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="text-field"
                placeholder="jean@example.com"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div>
              <TurnstileWidget onToken={setTurnstileToken} />
            </div>

            {error ? (
              <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting || !email || !turnstileToken}
            >
              {submitting ? "Envoi en cours..." : "Recevoir un lien magique"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
